/**
 * Tipo de armazenamento no Pedido de Saída (#MT-3902248 / #MT-2F9C150).
 * Refrigerado = flag do produto; Climatizado só com flag explícito falso.
 * Sem produto/flag → vazio (não chutar Climatizado).
 */

/**
 * @param {*} value
 * @returns {boolean|null}
 */
export function isRefrigeratedFlag(value)
{
	if (value === true || value === 1)
		return true;
	if (value === false || value === 0)
		return false;
	if (value == null)
		return null;
	const t = String(value).trim().toLowerCase();
	if (!t)
		return null;
	if (['1', 'true', 't', 'yes', 'sim'].includes(t))
		return true;
	if (['0', 'false', 'f', 'no', 'nao', 'não'].includes(t))
		return false;
	return null;
}

/**
 * @param {object|null|undefined} product
 * @returns {'REFRIGERADO'|'CLIMATIZADO'|null}
 */
export function storageArmCodeFromProduct(product)
{
	if (!product || typeof product !== 'object')
		return null;
	if (!Object.prototype.hasOwnProperty.call(product, 'is_refrigerated'))
		return null;
	const flag = isRefrigeratedFlag(product.is_refrigerated);
	if (flag === true)
		return 'REFRIGERADO';
	if (flag === false)
		return 'CLIMATIZADO';
	return null;
}

/**
 * Prefere o flag vindo no item da API; senão o produto do IndexedDB.
 * @param {object|null|undefined} item
 * @param {object|null|undefined} cachedProduct
 * @returns {'REFRIGERADO'|'CLIMATIZADO'|null}
 */
export function storageArmCodeFromItemAndProduct(item, cachedProduct)
{
	if (item && Object.prototype.hasOwnProperty.call(item, 'is_refrigerated') && item.is_refrigerated != null && item.is_refrigerated !== '')
	{
		const fromItem = storageArmCodeFromProduct({ is_refrigerated: item.is_refrigerated });
		if (fromItem)
			return fromItem;
	}
	return storageArmCodeFromProduct(cachedProduct || null);
}

/**
 * @param {'REFRIGERADO'|'CLIMATIZADO'|null|undefined} code
 * @returns {string}
 */
export function storageArmLabel(code)
{
	if (code === 'REFRIGERADO')
		return 'Refrigerado';
	if (code === 'CLIMATIZADO')
		return 'Climatizado';
	return '';
}

/**
 * @param {string|null|undefined} label
 * @returns {'REFRIGERADO'|'CLIMATIZADO'|null}
 */
export function storageArmCodeFromLabel(label)
{
	const t = String(label || '').trim().toUpperCase();
	if (t === 'REFRIGERADO')
		return 'REFRIGERADO';
	if (t === 'CLIMATIZADO')
		return 'CLIMATIZADO';
	return null;
}

/**
 * @param {Iterable<'REFRIGERADO'|'CLIMATIZADO'|null|undefined>} codes
 * @returns {boolean}
 */
export function hasMixedStorageArmCodes(codes)
{
	let hasRef = false;
	let hasClim = false;
	for (const code of codes || [])
	{
		if (code === 'REFRIGERADO')
			hasRef = true;
		else if (code === 'CLIMATIZADO')
			hasClim = true;
		if (hasRef && hasClim)
			return true;
	}
	return false;
}

/**
 * Coleta códigos Arm. de linhas com quantidade > 0 (lógica do banner).
 * @param {Array<{ quantity?: number|string, storage_arm?: string|null, arm?: string|null }>} lines
 * @returns {Array<'REFRIGERADO'|'CLIMATIZADO'>}
 */
export function collectStorageArmCodesFromLines(lines)
{
	const codes = [];
	for (const line of lines || [])
	{
		const rawQty = line?.quantity;
		const qty = Number(
			typeof rawQty === 'string'
				? String(rawQty).replace(/\./g, '').replace(',', '.')
				: rawQty,
		);
		if (!(qty > 0))
			continue;
		const code = storageArmCodeFromLabel(line?.storage_arm ?? line?.arm);
		if (code)
			codes.push(code);
	}
	return codes;
}

/**
 * Mistura a partir de itens do pedido + mapa produto (flag refrigerado).
 * @param {Array<{ product_id?: *, quantity?: *, is_refrigerated?: * }>} items
 * @param {Map<number, object>|Record<number, object>} productsById
 * @returns {boolean}
 */
export function itemsHaveMixedStorageArm(items, productsById)
{
	const get = (pid) =>
	{
		if (!productsById)
			return null;
		if (typeof productsById.get === 'function')
			return productsById.get(pid);
		return productsById[pid] ?? productsById[String(pid)] ?? null;
	};
	const codes = [];
	for (const item of items || [])
	{
		const pid = Number(item?.product_id);
		const qty = Number(item?.quantity);
		if (!Number.isInteger(pid) || pid <= 0 || !(qty > 0))
			continue;
		const code = storageArmCodeFromItemAndProduct(item, get(pid));
		if (code)
			codes.push(code);
	}
	return hasMixedStorageArmCodes(codes);
}

export const STORAGE_ARM_BANNER_TEXT =
	'Atenção: este pedido mistura itens Climatizados e Refrigerados. Separe em pedidos distintos antes de gerar a saída, ou confirme o salvamento ciente da mistura.';
